const STOREFRONT_API_VERSION = '2026-04';

if (!customElements.get('event-checkout-form')) {
  class EventCheckoutForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.section = this.closest('.shopify-section, dialog');
      this.quantityInput = this.querySelector('input[name="quantity"]');
      this.variantInput = this.querySelector('input[name="variant_id"]');
      this.countrySelect = this.querySelector('select[name="attributes[nazione]"]');
      this.submitButton = this.querySelector('[data-submit-button]');
      this.submitButtonText = this.querySelector('[data-submit-button-text]');
      this.errorMessage = this.querySelector('[data-error-message]');
      this.participantsContainer = this.querySelector('[data-participants-container]');
      this.addMemberButton = this.querySelector('[data-add-participant="socio"]');
      this.primaryFirstNameInput = this.querySelector('input[name="attributes[nome]"]');
      this.primaryLastNameInput = this.querySelector('input[name="attributes[cognome]"]');
      this.primaryTaxCodeInput = this.querySelector('input[name="attributes[codice_fiscale]"]');
      this.primaryMemberHelper = this.querySelector('[data-primary-member-help]');
      this.socioCheckUrl = this.dataset.socioCheckUrl || '';
      this.eventoHandle = this.dataset.eventoHandle || '';
      this.storefrontUrl = String(this.dataset.storefrontUrl || '').trim().replace(/\/+$/, '');
      this.storefrontToken = String(this.dataset.storefrontToken || '').trim();
      this.activeMemberRateUrl = '';
      this.exMemberRateUrl = '';
      this.cardValidationDelay = 700;
      this.cardValidationTimeouts = new Map();
      this.cardValidationControllers = new Map();
      this.cardValidationRequestId = 0;
      this.primaryMemberValidationTimeout = null;
      this.primaryMemberValidationController = null;
      this.primaryMemberValidationRequestId = 0;
      this.primaryMemberValidationStatus = 'idle';
      this.primaryMemberValidationCheckedValue = '';
      this.isSubmitting = false;
      this.isCheckoutDateValid = this.dataset.checkoutDateValid !== 'false';
      this.isVariantAvailable = false;
      this.participants = [];
      this.nextParticipantId = 1;

      this.defaultButtonText = this.submitButtonText?.textContent?.trim() || 'Procedi al checkout';
      this.loadingButtonText = this.getAttribute('data-loading-label') || 'Reindirizzamento...';

      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
      this.handleAddMember = this.handleAddMember.bind(this);
      this.handleParticipantInput = this.handleParticipantInput.bind(this);
      this.handleParticipantAction = this.handleParticipantAction.bind(this);
      this.handlePrimaryMemberInput = this.handlePrimaryMemberInput.bind(this);

      this.form.addEventListener('submit', this.handleSubmit);
      this.section?.addEventListener('variant:update', this.handleVariantUpdate);
      this.addMemberButton?.addEventListener('click', this.handleAddMember);
      this.participantsContainer?.addEventListener('input', this.handleParticipantInput);
      this.participantsContainer?.addEventListener('click', this.handleParticipantAction);
      this.primaryFirstNameInput?.addEventListener('input', this.handlePrimaryMemberInput);
      this.primaryLastNameInput?.addEventListener('input', this.handlePrimaryMemberInput);
      this.primaryTaxCodeInput?.addEventListener('input', this.handlePrimaryMemberInput);

      this.setDefaultCountry();
      this.setInitialVariantState();
      this.loadCrossShopEventoLinks();
      this.syncQuantityFromParticipants();
      this.renderParticipants();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.section?.removeEventListener('variant:update', this.handleVariantUpdate);
      this.addMemberButton?.removeEventListener('click', this.handleAddMember);
      this.participantsContainer?.removeEventListener('input', this.handleParticipantInput);
      this.participantsContainer?.removeEventListener('click', this.handleParticipantAction);
      this.primaryFirstNameInput?.removeEventListener('input', this.handlePrimaryMemberInput);
      this.primaryLastNameInput?.removeEventListener('input', this.handlePrimaryMemberInput);
      this.primaryTaxCodeInput?.removeEventListener('input', this.handlePrimaryMemberInput);
      this.clearPrimaryMemberValidationArtifacts();
      this.clearAllParticipantValidationArtifacts();
    }

    getPrimaryMemberValues() {
      return {
        nome: String(this.primaryFirstNameInput?.value || '').trim(),
        cognome: String(this.primaryLastNameInput?.value || '').trim(),
        codfisc: String(this.primaryTaxCodeInput?.value || '').trim(),
      };
    }

    getPrimaryMemberValidationKey(values = this.getPrimaryMemberValues()) {
      return JSON.stringify(values);
    }

    resolveMemberValidationStatus(data) {
      if (data?.success !== true) return 'error';

      const socioValue = Number(data?.data?.socio);
      if (socioValue === 1) return 'active-member';
      if (socioValue === 2) return 'ex-member';
      return 'valid';
    }

    async fetchCrossShopEventoLinks() {
      const endpoint = `${this.storefrontUrl}/api/${STOREFRONT_API_VERSION}/graphql.json`;
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.storefrontToken,
        },
        body: JSON.stringify({
          query: `query GetEvento($handle: String!) {
            metaobject(handle: {type: "eventi", handle: $handle}) {
              quotaSociAttivi: field(key: "quota_soci_attivi") {
                reference {
                  ... on Product {
                    onlineStoreUrl
                  }
                }
              }
              quotaSociNonAttivi: field(key: "quota_soci_non_attivi") {
                reference {
                  ... on Product {
                    onlineStoreUrl
                  }
                }
              }
            }
          }`,
          variables: { handle: this.eventoHandle },
        }),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const payload = await response.json();
      if (payload.errors?.length) {
        throw new Error(payload.errors.map((error) => error.message).join(', '));
      }

      const metaobject = payload.data?.metaobject;
      return {
        activeMemberUrl: String(metaobject?.quotaSociAttivi?.reference?.onlineStoreUrl || '').trim(),
        exMemberUrl: String(metaobject?.quotaSociNonAttivi?.reference?.onlineStoreUrl || '').trim(),
      };
    }

    loadCrossShopEventoLinks() {
      if (!this.eventoHandle || !this.storefrontUrl || !this.storefrontToken) return;

      this.fetchCrossShopEventoLinks()
        .then(({ activeMemberUrl, exMemberUrl }) => {
          this.activeMemberRateUrl = activeMemberUrl;
          this.exMemberRateUrl = exMemberUrl;
          this.renderPrimaryMemberValidationMessage(this.primaryMemberValidationStatus);
          this.participants.forEach((participant) => this.applyParticipantCardValidationState(participant));
        })
        .catch((error) => {
          console.error('Impossibile recuperare i link cross-shop per l\'evento.', error);
        });
    }

    renderPrimaryMemberValidationMessage(status) {
      if (!(this.primaryMemberHelper instanceof HTMLElement)) return;

      this.primaryMemberHelper.replaceChildren();

      if (status === 'active-member') {
        this.primaryMemberHelper.append(
          'Dal controllo effettuato, risulta una iscrizione attiva ad Assosommelier. Puoi usufruire di una '
        );
        if (this.activeMemberRateUrl) {
          const link = document.createElement('a');
          link.href = this.activeMemberRateUrl;
          link.textContent = 'tariffa speciale dedicata ai soci';
          this.primaryMemberHelper.append(link);
        } else {
          this.primaryMemberHelper.append('tariffa speciale dedicata ai soci');
        }
        this.primaryMemberHelper.append('.');
      } else if (status === 'ex-member') {
        this.primaryMemberHelper.append(
          'Dal controllo effettuato, risulta un precedente iscrizione ad Assosommelier al momento non attiva. Puoi usufruire della '
        );
        if (this.exMemberRateUrl) {
          const link = document.createElement('a');
          link.href = this.exMemberRateUrl;
          link.textContent = 'tariffa destinata agli ex-soci';
          this.primaryMemberHelper.append(link);
        } else {
          this.primaryMemberHelper.append('tariffa destinata agli ex-soci');
        }
      } else if (status === 'error') {
        this.primaryMemberHelper.textContent = 'Impossibile verificare il Codice Fiscale. Riprova.';
      }

      this.primaryMemberHelper.classList.toggle('hidden', !['active-member', 'ex-member', 'error'].includes(status));
    }

    setPrimaryMemberValidationState(status, checkedValue = '') {
      this.primaryMemberValidationStatus = status;
      this.primaryMemberValidationCheckedValue = checkedValue;

      if (this.primaryTaxCodeInput instanceof HTMLInputElement) {
        const validityMessage = status === 'active-member'
          ? 'Risulti già socio attivo: seleziona la tariffa dedicata ai soci.'
          : status === 'ex-member'
            ? 'Risulti un ex-socio: seleziona la tariffa dedicata agli ex-soci.'
            : '';
        this.primaryTaxCodeInput.setCustomValidity(validityMessage);
      }

      this.renderPrimaryMemberValidationMessage(status);
      this.updateActionButtons();
      this.updateSubmitButtonState();
    }

    clearPrimaryMemberValidationArtifacts() {
      if (this.primaryMemberValidationTimeout) {
        window.clearTimeout(this.primaryMemberValidationTimeout);
        this.primaryMemberValidationTimeout = null;
      }

      if (this.primaryMemberValidationController) {
        this.primaryMemberValidationController.abort();
        this.primaryMemberValidationController = null;
      }
    }

    handlePrimaryMemberInput() {
      if (!(this.primaryTaxCodeInput instanceof HTMLInputElement)) return;

      this.clearPrimaryMemberValidationArtifacts();
      this.setPrimaryMemberValidationState('idle', '');

      const values = this.getPrimaryMemberValues();
      if (!values.nome || !values.cognome || !this.primaryTaxCodeInput.value.trim()) return;

      this.setPrimaryMemberValidationState('pending', '');
      this.primaryMemberValidationTimeout = window.setTimeout(() => {
        this.primaryMemberValidationTimeout = null;
        this.validatePrimaryMember();
      }, this.cardValidationDelay);
    }

    async validatePrimaryMember() {
      if (!(this.primaryTaxCodeInput instanceof HTMLInputElement)) return true;

      const values = this.getPrimaryMemberValues();
      if (!values.nome || !values.cognome || !this.primaryTaxCodeInput.value.trim()) return false;

      const validationKey = this.getPrimaryMemberValidationKey(values);
      if (
        this.primaryMemberValidationCheckedValue === validationKey &&
        ['valid', 'active-member', 'ex-member'].includes(this.primaryMemberValidationStatus)
      ) {
        return this.primaryMemberValidationStatus === 'valid';
      }

      this.clearPrimaryMemberValidationArtifacts();
      this.setPrimaryMemberValidationState('pending', '');

      const requestId = this.primaryMemberValidationRequestId + 1;
      this.primaryMemberValidationRequestId = requestId;
      const controller = new AbortController();
      this.primaryMemberValidationController = controller;

      try {
        if (!this.socioCheckUrl) throw new Error('URL verifica socio non disponibile.');

        const response = await fetch(this.socioCheckUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();
        if (requestId !== this.primaryMemberValidationRequestId) return false;
        if (validationKey !== this.getPrimaryMemberValidationKey()) return false;

        const status = this.resolveMemberValidationStatus(data);
        this.setPrimaryMemberValidationState(status, validationKey);
        return status === 'valid';
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return false;
        if (requestId !== this.primaryMemberValidationRequestId) return false;

        this.setPrimaryMemberValidationState('error', validationKey);
        return false;
      } finally {
        if (this.primaryMemberValidationController === controller) {
          this.primaryMemberValidationController = null;
        }
      }
    }

    handleVariantUpdate(event) {
      if (!event?.detail) return;

      if (event.detail.data?.newProduct?.id) {
        this.dataset.productId = event.detail.data.newProduct.id;
      } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
        return;
      }

      const variant = event.detail.resource;
      if (!variant) return;

      const inventoryQuantity = Number.parseInt(variant.inventory_quantity, 10);
      if (Number.isFinite(inventoryQuantity)) {
        this.dataset.availability = String(Math.max(0, inventoryQuantity));
      }

      if (this.variantInput) {
        this.variantInput.value = variant.id || '';
      }

      if (this.submitButton) {
        this.isVariantAvailable = Boolean(variant.available);
      }

      if (this.quantityInput) {
        const max = variant.quantity_rule?.max;
        this.quantityInput.min = '1';
        this.quantityInput.step = '1';

        if (max !== null && max !== undefined) {
          this.quantityInput.max = String(max);
        } else {
          this.quantityInput.removeAttribute('max');
        }
      }

      this.enforceParticipantLimits();
      this.syncQuantityFromParticipants();
      this.renderParticipants();
      this.updateSubmitButtonState();
    }

    getInitialVariantFromPicker() {
      const productId = this.dataset.productId;
      const scopedSelector = productId
        ? `variant-picker[data-product-id="${productId}"] script[type="application/json"]`
        : 'variant-picker script[type="application/json"]';
      const variantScript =
        this.section?.querySelector(scopedSelector) ||
        this.section?.querySelector('variant-picker script[type="application/json"]');

      if (!variantScript?.textContent) return null;

      try {
        return JSON.parse(variantScript.textContent);
      } catch (error) {
        console.warn('Unable to parse initial variant JSON for event checkout.', error);
        return null;
      }
    }

    setInitialVariantState() {
      const initialVariant = this.getInitialVariantFromPicker();

      if (initialVariant?.id && this.variantInput && !this.variantInput.value) {
        this.variantInput.value = String(initialVariant.id);
      }

      if (this.quantityInput) {
        const max = initialVariant?.quantity_rule?.max;
        this.quantityInput.min = '1';
        this.quantityInput.step = '1';

        if (max !== null && max !== undefined) {
          this.quantityInput.max = String(max);
        } else {
          this.quantityInput.removeAttribute('max');
        }
      }

      if (typeof initialVariant?.available === 'boolean') {
        this.isVariantAvailable = initialVariant.available;
        return;
      }

      this.isVariantAvailable = Boolean(this.variantInput?.value);
    }

    setDefaultCountry() {
      if (!this.countrySelect) return;
      const preferred = this.dataset.defaultCountry || 'Italy';
      const fallback = preferred === 'Italy' ? 'Italia' : 'Italy';

      if (this.countrySelect.value && this.countrySelect.value !== '---') return;

      if (this.countrySelect.querySelector(`option[value="${preferred}"]`)) {
        this.countrySelect.value = preferred;
        return;
      }

      if (this.countrySelect.querySelector(`option[value="${fallback}"]`)) {
        this.countrySelect.value = fallback;
      }
    }

    getQuantity() {
      const parsedQuantity = Number.parseInt(this.quantityInput?.value || '1', 10);
      return Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
    }

    getAvailability() {
      const availability = Number.parseInt(this.dataset.availability || '', 10);
      return Number.isFinite(availability) && availability >= 0 ? availability : null;
    }

    getMaxQuantity() {
      const variantMax = Number.parseInt(this.quantityInput?.getAttribute('max') || '', 10);
      const limits = [
        this.getAvailability(),
        Number.isFinite(variantMax) && variantMax > 0 ? variantMax : null,
      ].filter((limit) => limit !== null);

      return limits.length > 0 ? Math.min(...limits) : null;
    }

    canIncreaseQuantity() {
      const maxQuantity = this.getMaxQuantity();
      if (maxQuantity === null) return true;

      return this.participants.length + 2 <= maxQuantity;
    }

    canAddParticipant() {
      if (!this.canIncreaseQuantity()) return false;

      // Nessun nuovo partecipante finché il partecipante principale e tutti i partecipanti
      // aggiuntivi già presenti non risultano non-socio verificati.
      if (this.primaryMemberValidationStatus !== 'valid') return false;

      const hasNonCompliantParticipant = this.participants.some(
        (participant) => participant.cardValidationStatus !== 'valid'
      );
      if (hasNonCompliantParticipant) return false;

      return true;
    }

    syncQuantityFromParticipants() {
      if (!this.quantityInput) return;

      this.quantityInput.value = String(this.participants.length + 1);
    }

    enforceParticipantLimits() {
      const maxQuantity = this.getMaxQuantity();
      const maxExtras = maxQuantity === null ? null : Math.max(maxQuantity - 1, 0);

      if (maxExtras !== null && this.participants.length > maxExtras) {
        this.participants = this.participants.slice(0, maxExtras);
      }

      this.cleanupRemovedParticipantValidationArtifacts();
    }

    renderParticipants() {
      if (!this.participantsContainer) return;

      if (this.participants.length === 0) {
        this.participantsContainer.innerHTML = '';
        this.participantsContainer.classList.add('hidden');
        this.updateActionButtons();
        this.updateSubmitButtonState();
        return;
      }

      this.participantsContainer.innerHTML = '';
      const fragment = document.createDocumentFragment();
      const members = this.participants.filter((participant) => participant.type === 'socio');

      if (members.length > 0) {
        fragment.appendChild(this.createParticipantGroup('Partecipanti aggiuntivi', members));
      }

      this.participantsContainer.appendChild(fragment);
      this.participantsContainer.classList.remove('hidden');
      this.updateActionButtons();
      this.updateSubmitButtonState();
    }

    createParticipantGroup(titleText, participants) {
      const group = document.createElement('div');
      group.className = 'course-checkout-block__participant-group';

      const title = document.createElement('p');
      title.className = 'course-checkout-block__participant-group-title';
      title.textContent = titleText;
      group.appendChild(title);

      participants.forEach((participant, index) => {
        group.appendChild(this.createParticipantFieldset(participant, index + 1));
      });

      return group;
    }

    createParticipantFieldset(participant, participantIndex) {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'course-checkout-block__participant';

      const header = document.createElement('div');
      header.className = 'course-checkout-block__participant-header';

      const title = document.createElement('p');
      title.className = 'course-checkout-block__participant-title';
      title.textContent = `Partecipante aggiuntivo #${participantIndex}`;
      header.appendChild(title);

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'course-checkout-block__participant-remove button-unstyled';
      removeButton.dataset.removeParticipant = String(participant.id);
      removeButton.textContent = 'Rimuovi';
      header.appendChild(removeButton);

      fieldset.appendChild(header);

      const fields = document.createElement('div');
      fields.className = 'course-checkout-block__participant-fields';

      fields.appendChild(this.createParticipantField(participant, 'firstName', 'Nome', 'given-name'));
      fields.appendChild(this.createParticipantField(participant, 'lastName', 'Cognome', 'family-name'));
      fields.appendChild(this.createParticipantField(participant, 'taxCode', 'Codice Fiscale', 'off', true));

      fieldset.appendChild(fields);
      return fieldset;
    }

    createParticipantField(participant, fieldName, labelText, autocomplete, isFullWidth = false) {
      const wrapper = document.createElement('div');
      wrapper.className = 'course-checkout-block__participant-field';
      if (isFullWidth) {
        wrapper.classList.add('course-checkout-block__participant-field--full');
      }

      const safeFieldName = `participant_${participant.id}_${fieldName}`;
      const isTaxCodeField = fieldName === 'taxCode';

      const label = document.createElement('label');
      label.setAttribute('for', `${this.dataset.blockId}-${safeFieldName}`);
      label.textContent = labelText;

      const input = document.createElement('input');
      input.id = `${this.dataset.blockId}-${safeFieldName}`;
      input.name = safeFieldName;
      input.type = 'text';
      input.autocomplete = autocomplete;
      input.dataset.participantId = String(participant.id);
      input.dataset.participantField = fieldName;
      input.value = participant[fieldName] || '';
      input.required = true;

      if (isTaxCodeField) {
        input.autocapitalize = 'characters';
      }

      wrapper.appendChild(label);
      const inputWrapper = document.createElement('div');
      inputWrapper.className = 'course-checkout-block__participant-input-wrapper';
      inputWrapper.appendChild(input);

      let loader = null;
      if (isTaxCodeField) {
        loader = document.createElement('span');
        loader.className = 'course-checkout-block__participant-loader hidden';
        loader.dataset.participantId = String(participant.id);
        loader.dataset.participantLoader = 'taxCode';
        loader.setAttribute('aria-hidden', 'true');
        inputWrapper.appendChild(loader);
      }

      wrapper.appendChild(inputWrapper);

      let helper = null;
      if (isTaxCodeField) {
        helper = document.createElement('p');
        helper.className = 'course-checkout-block__participant-help hidden';
        helper.dataset.participantId = String(participant.id);
        helper.dataset.participantHelp = 'taxCode';
        helper.setAttribute('aria-live', 'polite');
        wrapper.appendChild(helper);
      }

      if (isTaxCodeField) {
        this.applyParticipantCardValidationState(participant, input, loader, helper);
      }

      return wrapper;
    }

    handleAddMember() {
      this.addParticipant('socio');
    }

    addParticipant(type) {
      if (!this.canAddParticipant(type)) return;

      this.participants.push({
        id: this.nextParticipantId,
        type,
        firstName: '',
        lastName: '',
        taxCode: '',
        cardValidationStatus: 'idle',
        cardValidationCheckedValue: '',
        cardValidationRequestId: 0,
      });
      this.nextParticipantId += 1;

      this.syncQuantityFromParticipants();
      this.renderParticipants();
    }

    handleParticipantInput(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;

      const participantId = Number.parseInt(input.dataset.participantId || '', 10);
      const participantField = input.dataset.participantField;
      if (!Number.isFinite(participantId) || !participantField) return;

      const participant = this.participants.find((entry) => entry.id === participantId);
      if (!participant) return;

      participant[participantField] = input.value;

      if (['firstName', 'lastName', 'taxCode'].includes(participantField)) {
        this.scheduleParticipantCardValidation(participant);
      }
    }

    getParticipantCardInput(participantId) {
      return this.querySelector(`input[data-participant-id="${participantId}"][data-participant-field="taxCode"]`);
    }

    getParticipantCardLoader(participantId) {
      return this.querySelector(`[data-participant-id="${participantId}"][data-participant-loader="taxCode"]`);
    }

    getParticipantCardHelper(participantId) {
      return this.querySelector(`[data-participant-id="${participantId}"][data-participant-help="taxCode"]`);
    }

    getParticipantMemberValues(participant) {
      return {
        nome: String(participant.firstName || '').trim(),
        cognome: String(participant.lastName || '').trim(),
        codfisc: String(participant.taxCode || '').trim(),
      };
    }

    getParticipantMemberValidationKey(participant) {
      return JSON.stringify(this.getParticipantMemberValues(participant));
    }

    getParticipantCardValidationMessage(participant) {
      if (participant.cardValidationStatus === 'active-member') {
        return 'Risulti già socio attivo: seleziona la tariffa dedicata ai soci.';
      }
      if (participant.cardValidationStatus === 'ex-member') {
        return 'Risulti un ex-socio: seleziona la tariffa dedicata agli ex-soci.';
      }
      if (participant.cardValidationStatus === 'error') {
        return 'Impossibile verificare il Codice Fiscale.';
      }

      return '';
    }

    getParticipantCardHelperMessage(participant) {
      const validationValue = String(participant.taxCode || '').trim();
      if (validationValue === '') return '';
      if (participant.cardValidationStatus === 'active-member') {
        return 'Dal controllo effettuato, risulta una iscrizione attiva ad Assosommelier. Puoi usufruire di una ';
      }
      if (participant.cardValidationStatus === 'ex-member') {
        return 'Dal controllo effettuato, risulta un precedente iscrizione ad Assosommelier al momento non attiva. Puoi usufruire della ';
      }
      if (participant.cardValidationStatus === 'error') {
        return 'Impossibile verificare il Codice Fiscale.';
      }

      return '';
    }

    applyParticipantCardValidationState(
      participant,
      input = this.getParticipantCardInput(participant.id),
      loader = this.getParticipantCardLoader(participant.id),
      helper = this.getParticipantCardHelper(participant.id)
    ) {
      if (!(input instanceof HTMLInputElement)) return;

      const message = this.getParticipantCardValidationMessage(participant);
      input.setCustomValidity(message);

      const fieldWrapper = input.closest('.course-checkout-block__participant-field');
      const isPending = participant.cardValidationStatus === 'pending';
      const helperMessage = this.getParticipantCardHelperMessage(participant);

      fieldWrapper?.classList.toggle('course-checkout-block__participant-field--loading', isPending);
      loader?.classList.toggle('hidden', !isPending);

      if (helper instanceof HTMLElement) {
        helper.replaceChildren();
        helper.append(helperMessage);

        if (participant.cardValidationStatus === 'active-member') {
          if (this.activeMemberRateUrl) {
            const link = document.createElement('a');
            link.href = this.activeMemberRateUrl;
            link.textContent = 'tariffa speciale dedicata ai soci';
            helper.append(link);
          } else {
            helper.append('tariffa speciale dedicata ai soci');
          }
          helper.append('.');
        } else if (participant.cardValidationStatus === 'ex-member') {
          if (this.exMemberRateUrl) {
            const link = document.createElement('a');
            link.href = this.exMemberRateUrl;
            link.textContent = 'tariffa destinata agli ex-soci';
            helper.append(link);
          } else {
            helper.append('tariffa destinata agli ex-soci');
          }
        }

        helper.classList.toggle('hidden', helperMessage === '');
      }
    }

    setParticipantCardValidationState(participant, status, checkedValue = '') {
      participant.cardValidationStatus = status;
      participant.cardValidationCheckedValue = checkedValue;
      this.applyParticipantCardValidationState(participant);
      this.updateActionButtons();
      this.updateSubmitButtonState();
    }

    clearParticipantValidationTimer(participantId) {
      const timeoutId = this.cardValidationTimeouts.get(participantId);
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        this.cardValidationTimeouts.delete(participantId);
      }
    }

    abortParticipantValidationRequest(participantId) {
      const controller = this.cardValidationControllers.get(participantId);
      if (controller) {
        controller.abort();
        this.cardValidationControllers.delete(participantId);
      }
    }

    clearParticipantValidationArtifacts(participantId) {
      this.clearParticipantValidationTimer(participantId);
      this.abortParticipantValidationRequest(participantId);
    }

    clearAllParticipantValidationArtifacts() {
      Array.from(this.cardValidationTimeouts.keys()).forEach((participantId) => {
        this.clearParticipantValidationTimer(participantId);
      });
      Array.from(this.cardValidationControllers.keys()).forEach((participantId) => {
        this.abortParticipantValidationRequest(participantId);
      });
    }

    cleanupRemovedParticipantValidationArtifacts() {
      const activeParticipantIds = new Set(this.participants.map((participant) => participant.id));

      Array.from(this.cardValidationTimeouts.keys()).forEach((participantId) => {
        if (!activeParticipantIds.has(participantId)) {
          this.clearParticipantValidationTimer(participantId);
        }
      });

      Array.from(this.cardValidationControllers.keys()).forEach((participantId) => {
        if (!activeParticipantIds.has(participantId)) {
          this.abortParticipantValidationRequest(participantId);
        }
      });
    }

    hasPendingCardValidation() {
      return this.participants.some((participant) => {
        if (String(participant.taxCode || '').trim() === '') return false;

        return participant.cardValidationStatus === 'pending';
      });
    }

    scheduleParticipantCardValidation(participant) {
      const values = this.getParticipantMemberValues(participant);
      this.clearParticipantValidationTimer(participant.id);
      this.abortParticipantValidationRequest(participant.id);

      if (!values.nome || !values.cognome || !values.codfisc) {
        this.setParticipantCardValidationState(participant, 'idle', '');
        return;
      }

      this.setParticipantCardValidationState(participant, 'pending', '');

      const timeoutId = window.setTimeout(() => {
        this.cardValidationTimeouts.delete(participant.id);
        this.validateParticipantCard(participant.id);
      }, this.cardValidationDelay);

      this.cardValidationTimeouts.set(participant.id, timeoutId);
    }

    async validateParticipantCard(participantId) {
      const participant = this.participants.find((entry) => entry.id === participantId);
      if (!participant) return;

      const values = this.getParticipantMemberValues(participant);
      if (!values.nome || !values.cognome || !values.codfisc) {
        this.setParticipantCardValidationState(participant, 'idle', '');
        return;
      }
      const validationKey = this.getParticipantMemberValidationKey(participant);

      const requestId = this.cardValidationRequestId + 1;
      this.cardValidationRequestId = requestId;
      participant.cardValidationRequestId = requestId;

      const controller = new AbortController();
      this.cardValidationControllers.set(participantId, controller);

      try {
        const response = await fetch(this.socioCheckUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(values),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        const currentParticipant = this.participants.find((entry) => entry.id === participantId);
        if (!currentParticipant) return;

        const currentValidationKey = this.getParticipantMemberValidationKey(currentParticipant);
        if (currentParticipant.cardValidationRequestId !== requestId || currentValidationKey !== validationKey) return;

        this.setParticipantCardValidationState(currentParticipant, this.resolveMemberValidationStatus(data), validationKey);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;

        const currentParticipant = this.participants.find((entry) => entry.id === participantId);
        if (!currentParticipant) return;

        const currentValidationKey = this.getParticipantMemberValidationKey(currentParticipant);
        if (currentParticipant.cardValidationRequestId !== requestId || currentValidationKey !== validationKey) return;

        this.setParticipantCardValidationState(currentParticipant, 'error', validationKey);
      } finally {
        const activeController = this.cardValidationControllers.get(participantId);
        if (activeController === controller) {
          this.cardValidationControllers.delete(participantId);
        }
      }
    }

    handleParticipantAction(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const removeButton = target.closest('[data-remove-participant]');
      if (!(removeButton instanceof HTMLElement)) return;

      const participantId = Number.parseInt(removeButton.dataset.removeParticipant || '', 10);
      if (!Number.isFinite(participantId)) return;

      this.clearParticipantValidationArtifacts(participantId);
      this.participants = this.participants.filter((participant) => participant.id !== participantId);
      this.syncQuantityFromParticipants();
      this.renderParticipants();
      this.updateSubmitButtonState();
    }

    updateActionButtons() {
      if (this.addMemberButton) {
        this.addMemberButton.disabled = !this.canAddParticipant();
      }
    }

    updateSubmitButtonState() {
      if (!this.submitButton) return;

      const hasVariantId = Boolean(this.variantInput?.value);
      const maxQuantity = this.getMaxQuantity();
      const shouldDisable =
        this.isSubmitting ||
        !this.isCheckoutDateValid ||
        !hasVariantId ||
        !this.isVariantAvailable ||
        (maxQuantity !== null && this.getQuantity() > maxQuantity) ||
        this.primaryMemberValidationStatus === 'pending' ||
        this.hasPendingCardValidation();

      this.submitButton.disabled = shouldDisable;
    }

    showError(message) {
      if (!this.errorMessage) return;
      this.errorMessage.textContent = message;
      this.errorMessage.classList.remove('hidden');
    }

    clearError() {
      if (!this.errorMessage) return;
      this.errorMessage.textContent = '';
      this.errorMessage.classList.add('hidden');
    }

    setSubmittingState(isSubmitting) {
      if (!this.submitButton) return;

      this.isSubmitting = isSubmitting;
      this.updateSubmitButtonState();
      this.submitButton.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');

      if (this.submitButtonText) {
        this.submitButtonText.textContent = isSubmitting ? this.loadingButtonText : this.defaultButtonText;
      }
    }

    buildExtraParticipantAttributeEntries() {
      const attributes = [];
      let participantIndex = 0;

      for (const participant of this.participants) {
        const firstName = String(participant.firstName || '').trim();
        const lastName = String(participant.lastName || '').trim();
        const taxCode = String(participant.taxCode || '').trim();

        participantIndex += 1;
        if (firstName) {
          attributes.push([`partecipante_aggiuntivo_${participantIndex}_nome`, firstName]);
        }
        if (lastName) {
          attributes.push([`partecipante_aggiuntivo_${participantIndex}_cognome`, lastName]);
        }
        if (taxCode) {
          attributes.push([`partecipante_aggiuntivo_${participantIndex}_codice_fiscale`, taxCode]);
        }
      }

      return attributes;
    }

    extractAttributeName(fieldName) {
      if (typeof fieldName !== 'string') return null;
      if (!fieldName.startsWith('attributes[') || !fieldName.endsWith(']')) return null;

      return fieldName.slice(11, -1).trim();
    }

    buildCartAttributeEntries() {
      const formData = new FormData(this.form);
      const attributes = [];

      for (const [fieldName, value] of formData.entries()) {
        const attributeName = this.extractAttributeName(fieldName);
        if (!attributeName) continue;

        const normalizedValue = String(value || '').trim();
        if (normalizedValue !== '') {
          attributes.push([attributeName, normalizedValue]);
        }
      }

      attributes.push(...this.buildExtraParticipantAttributeEntries());

      return attributes;
    }

    buildDraftOrderAttributes() {
      const attributes = this.buildCartAttributeEntries().reduce((payload, [attributeName, attributeValue]) => {
        payload[attributeName] = attributeValue;
        return payload;
      }, {});

      attributes.buyer_email = attributes.email || '';
      return attributes;
    }

    extractPropertyName(fieldName) {
      if (typeof fieldName !== 'string') return null;
      if (!fieldName.startsWith('properties[') || !fieldName.endsWith(']')) return null;

      return fieldName.slice(11, -1).trim();
    }

    buildLineItemPropertiesPayload() {
      const allowedPropertyNames = [
        '_sede_ed_edizione',
        '_entity_type',
        '_titolo',
        '_tariffa',
        '_data_inizio',
      ];
      const formData = new FormData(this.form);
      const properties = {};

      for (const [fieldName, value] of formData.entries()) {
        const propertyName = this.extractPropertyName(fieldName);
        if (!propertyName || !allowedPropertyNames.includes(propertyName)) continue;

        const normalizedValue = String(value || '').trim();
        if (normalizedValue !== '') {
          properties[propertyName] = normalizedValue;
        }
      }

      return properties;
    }

    getCountryCode(countryName) {
      const normalizeCountryName = (value) => String(value || '')
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase()
        .replace(/[^a-z0-9]/g, '');
      const normalizedCountryName = normalizeCountryName(countryName);
      const countryEntries = this.querySelectorAll('[data-country-code-map] [data-country-name]');

      for (const countryEntry of countryEntries) {
        if (normalizeCountryName(countryEntry.dataset.countryName) === normalizedCountryName) {
          return String(countryEntry.dataset.countryCode || '').trim().toUpperCase();
        }
      }

      if (normalizedCountryName === 'italy' || normalizedCountryName === 'italia') {
        return 'IT';
      }

      return '';
    }

    buildDraftOrderPayload(variantId, quantity) {
      const attributes = this.buildDraftOrderAttributes();
      const phoneNumber = String(attributes.telefono || '').trim();
      const countryCode = this.getCountryCode(attributes.nazione);

      if (phoneNumber.replace(/\D/g, '').length < 7) {
        throw new Error('Inserisci un numero di telefono valido.');
      }

      const billingAddress = {
        address1: attributes.indirizzo || '',
        city: attributes.citta || '',
        province_code: attributes.provincia || '',
        postal_code: attributes.cap || '',
      };

      if (countryCode) {
        billingAddress.country_code = countryCode;
      }

      return {
        store: this.dataset.draftOrderStore || 'main',
        draft_order: {
          customer: {
            first_name: attributes.nome || '',
            last_name: attributes.cognome || '',
            email: attributes.email || '',
            phone: phoneNumber,
          },
          billing_address: billingAddress,
          shipping_address: { ...billingAddress },
          line_items: [
            {
              variant_id: variantId,
              properties: this.buildLineItemPropertiesPayload(),
              quantity,
            },
          ],
          attributes,
        },
      };
    }

    getRootPath() {
      const rootPath = window.Shopify?.routes?.root || '/';
      return rootPath.endsWith('/') ? rootPath : `${rootPath}/`;
    }

    getCartClearUrl() {
      return `${this.getRootPath()}cart/clear.js`;
    }

    async fetchWithTimeout(url, options, timeout = 15000) {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), timeout);

      try {
        return await fetch(url, {
          ...options,
          signal: controller.signal,
        });
      } finally {
        window.clearTimeout(timeoutId);
      }
    }

    async clearCart() {
      const response = await this.fetchWithTimeout(this.getCartClearUrl(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: '{}',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
    }

    getApiErrorMessage(response, data) {
      const message = String(data?.error || data?.message || data?.details?.message || '').trim();
      const errorText = `${message} ${JSON.stringify(data?.details || {})}`;

      if (/variant|inventory|available|sold.?out|esaurit|non disponibile/i.test(errorText)) {
        return 'La variante selezionata non è disponibile o è esaurita.';
      }
      if (/phone.*invalid|invalid.*phone|telefono.*non valid/i.test(errorText)) {
        return 'Il numero di telefono non è valido. Verifica il numero e la nazione selezionata.';
      }

      return message || `Non è stato possibile creare il checkout (HTTP ${response.status}).`;
    }

    async createDraftOrder(payload) {
      const response = await this.fetchWithTimeout(this.dataset.draftOrderEndpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      let data = null;

      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }

      if (!response.ok || data?.success !== true) {
        throw new Error(this.getApiErrorMessage(response, data));
      }

      const invoiceUrl = String(data?.data?.draft_order?.invoice_url || '').trim();
      let parsedInvoiceUrl = null;

      try {
        parsedInvoiceUrl = new URL(invoiceUrl);
      } catch (error) {
        parsedInvoiceUrl = null;
      }

      if (!parsedInvoiceUrl || parsedInvoiceUrl.protocol !== 'https:') {
        throw new Error('Il checkout è stato creato, ma Shopify non ha restituito un URL valido.');
      }

      return parsedInvoiceUrl.href;
    }

    async handleSubmit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.isSubmitting) return;
      if (!this.isCheckoutDateValid) return;
      if (!this.form.reportValidity()) return;

      const emailInput = this.form.querySelector('input[name="attributes[email]"]');
      const email = String(emailInput?.value || '').trim();
      if (!emailInput || !emailInput.validity.valid || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.showError('Inserisci un indirizzo e-mail valido.');
        emailInput?.focus();
        return;
      }

      const variantId = Number.parseInt(this.variantInput?.value || '', 10);
      const quantity = this.getQuantity();

      if (!Number.isFinite(variantId) || variantId <= 0) {
        this.showError('Variante non valida.');
        return;
      }
      if (!this.isVariantAvailable) {
        this.showError('La variante selezionata non è disponibile o è esaurita.');
        return;
      }
      if (this.getMaxQuantity() !== null && quantity > this.getMaxQuantity()) {
        this.showError('La quantità richiesta non è disponibile.');
        return;
      }

      this.clearError();
      this.setSubmittingState(true);

      try {
        const payload = this.buildDraftOrderPayload(variantId, quantity);

        try {
          await this.clearCart();
        } catch (cartError) {
          console.error('Impossibile svuotare il carrello Shopify prima del Draft Order.', cartError);
        }

        const invoiceUrl = await this.createDraftOrder(payload);
        window.location.assign(invoiceUrl);
      } catch (error) {
        const message = error?.name === 'AbortError'
          ? 'La richiesta ha impiegato troppo tempo. Riprova senza ricaricare la pagina.'
          : error instanceof Error
            ? error.message
            : 'Si è verificato un errore inatteso. Riprova.';
        this.showError(message);
        this.setSubmittingState(false);
      }
    }
  }

  customElements.define('event-checkout-form', EventCheckoutForm);
}
